/**
 * Scans — repo list stays put; opening a repo shows tabs, one per scan type
 * (see config/scanTypes.js), each with its own independent run history.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Play, Trash2, RefreshCw, Download, XCircle, ArrowLeft, FileText, Loader2,
  ExternalLink, Pencil, GitBranch, ChevronDown, KeyRound
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import formatApiError from '../../../utils/formatApiError';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import AlertModal from '../modals/AlertModal';
import ConfirmModal from '../modals/ConfirmModal';
import AddSecScanRepoModal from '../modals/AddSecScanRepoModal';
import EditSecScanRepoModal from '../modals/EditSecScanRepoModal';
import GrantSecScanAccessModal from '../modals/GrantSecScanAccessModal';
import { serviceAccountLabel } from '../components/ServiceAccountAccessSteps';
import { SCAN_TYPES, DEFAULT_SCAN_TYPE, getScanType } from '../../../config/scanTypes';
import config from '../../../config/environment';
import { encryptAccessTokenForTransport } from '../../../utils/secScanTokenCrypto';
import './GarageSecurityScanView.css';

const ACTIVE = new Set(['pending', 'claimed', 'running', 'publishing']);
const POLL_MS = 3000;

// Worker-internal statuses don't mean anything to a reader of the page.
const STATUS_LABELS = {
  pending: 'Queued',
  claimed: 'Starting',
  running: 'Scanning',
  publishing: 'Finalizing',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const PROVIDER_LABELS = { claude: 'Claude', codex: 'Codex' };

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const LISTED_SEVERITIES = ['critical', 'high'];

const statusLabel = (status) => STATUS_LABELS[status] || status || 'Unknown';

const providerLabel = (provider) => PROVIDER_LABELS[provider] || provider || '—';

const statusClass = (status) => {
  if (status === 'succeeded') return 'garage-status-badge published';
  if (status === 'failed') return 'garage-status-badge rejected';
  if (status === 'cancelled') return 'garage-status-badge';
  return 'garage-status-badge pending';
};

/**
 * The API returns naive UTC (Python datetime.utcnow().isoformat(), no offset),
 * and JS parses a timezone-less date-time as LOCAL time. Without this every
 * timestamp is wrong by the viewer's UTC offset.
 */
const toDate = (value) => {
  if (!value) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const d = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatWhen = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleString() : '—';
};

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '—';
  const ms = toDate(finishedAt) - toDate(startedAt);
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  return `${Math.floor(totalSec / 60)}m ${totalSec % 60}s`;
};

const isActive = (run) => Boolean(run) && ACTIVE.has(run.status);

const DEFAULT_BRANCH_LABEL = 'default branch';

const branchLabel = (ref) => (ref || '').trim() || DEFAULT_BRANCH_LABEL;

// Why the Run button is disabled, phrased as an instruction.
const blockedReason = (activeRun) => {
  if (!activeRun) return null;
  return (
    `A scan of ${branchLabel(activeRun.ref)} is already ${statusLabel(activeRun.status).toLowerCase()}. `
    + 'To scan a different branch, edit the repository and change the branch.'
  );
};

const ACCESS_BLOCK = 'Grant repository access before scanning.';
const ACCESS_WAIT = 'Wait for repository access to be verified before scanning.';
const ACCESS_PENDING = new Set(['pending', 'checking']);

const accessStatusOf = (repo) => (repo?.accessStatus || '').trim();

const scanDisabledForAccess = (repo, accessImmediate) => {
  const status = accessStatusOf(repo);
  if (status === 'denied') return true;
  if (ACCESS_PENDING.has(status) && accessImmediate) return true;
  return false;
};

const scanBlockedReason = (repo, skillKey, accessImmediate) => {
  const status = accessStatusOf(repo);
  if (status === 'denied') return ACCESS_BLOCK;
  if (ACCESS_PENDING.has(status) && accessImmediate) return ACCESS_WAIT;
  return blockedReason(repo?.activeRunForCurrentRefBySkill?.[skillKey]);
};

const AccessHint = ({ repo, accessImmediate, serviceUser }) => {
  const status = accessStatusOf(repo);
  const account = serviceAccountLabel(serviceUser);
  if (status === 'verified') {
    return (
      <div className="sec-scan-access-hint">
        <p className="sec-scan-access-status is-ok">Access verified</p>
      </div>
    );
  }
  if (ACCESS_PENDING.has(status)) {
    return (
      <div className="sec-scan-access-hint">
        <p className="sec-scan-access-status is-pending">
          {accessImmediate ? 'Checking access…' : 'Checking for access'}
        </p>
        {!accessImmediate && (
          <p className="sec-scan-access-note">Access will be checked when the scan runs</p>
        )}
      </div>
    );
  }
  if (status === 'denied') {
    return (
      <div className="sec-scan-access-hint" role="alert">
        <p className="sec-scan-access-status is-denied">Access denied — need access</p>
        <p className="sec-scan-access-note">
          Invite <strong>{account}</strong> on the GitLab project as role{' '}
          <strong>Reporter</strong> (minimum). Or paste a token with scope{' '}
          <code>read_repository</code> and role Reporter.
        </p>
        {repo?.accessMessage ? (
          <p className="sec-scan-access-note">{repo.accessMessage}</p>
        ) : null}
      </div>
    );
  }
  return null;
};

const MINUTE = 60 * 1000;

const formatGap = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return secs ? `${mins}m ${secs}s` : `${mins}m`;
};

/**
 * ETA for a queued scan with VM workers on a fixed cron:
 *   lastWorkerActivity + interval + docker buffer
 * When that time is in the past, roll forward by `interval` (next cron tick)
 * so the counter restarts instead of going stale.
 */
const pickupEstimate = (worker, now, queuedAt) => {
  const intervalMin = Number(worker?.workerIntervalMinutes);
  if (!Number.isFinite(intervalMin) || intervalMin <= 0) return null;

  const bufferMin = Math.max(0, Number(worker?.workerBufferMinutes) || 0);
  const intervalMs = intervalMin * MINUTE;
  const bufferMs = bufferMin * MINUTE;
  const skewMs = worker?.skewMs || 0;
  const serverNow = now - skewMs;

  const activity = toDate(worker?.lastWorkerActivityAt);
  const queued = toDate(queuedAt);
  // Prefer last worker claim/start/finish; else when this job was queued; else now.
  const anchorMs = (activity || queued || new Date(serverNow)).getTime();

  // First window includes docker buffer once; later rolls use interval only.
  let etaMs = anchorMs + intervalMs + bufferMs;
  let cycles = 0;
  while (etaMs <= serverNow && cycles < 10_000) {
    etaMs += intervalMs;
    cycles += 1;
  }

  return {
    remainingMs: Math.max(0, etaMs - serverNow),
    intervalMs,
    bufferMs,
    cycles,
    anchor: new Date(anchorMs),
    eta: new Date(etaMs),
  };
};

const PickupEstimate = ({ worker, now, queuedAt }) => {
  const est = pickupEstimate(worker, now, queuedAt);
  if (!est) {
    return (
      <span className="sec-scan-eta" title="VM workers claim pending jobs on their schedule">
        Queued for worker
      </span>
    );
  }

  const explain = [
    `Last worker activity: ${est.anchor.toLocaleString()}`,
    `+ schedule every ${formatGap(est.intervalMs)}`,
    est.bufferMs ? `+ ${formatGap(est.bufferMs)} Docker start buffer` : null,
    est.cycles ? `(rolled forward ${est.cycles} schedule cycle(s))` : null,
    `≈ ${est.eta.toLocaleTimeString()}`,
    '',
    'Estimate only — workers must be free and the next cron tick must run.',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span className="sec-scan-eta" title={explain}>
      {est.remainingMs > 0
        ? `starts in ~${formatGap(est.remainingMs)}`
        : 'starting shortly'}
    </span>
  );
};

// Prop is `gitRef`, not `ref`: React reserves `ref` and would never pass it through.
const BranchTag = ({ gitRef }) => (
  <span className="sec-scan-branch" title={`Branch: ${branchLabel(gitRef)}`}>
    <GitBranch size={11} />
    {branchLabel(gitRef)}
  </span>
);

const SeverityChips = ({ counts, only }) => {
  const keys = (only || SEVERITIES).filter((key) => counts?.[key]);
  if (!keys.length) return null;
  return (
    <>
      {keys.map((key) => (
        <span key={key} className={`sec-scan-sev sec-scan-sev-${key}`}>
          {counts[key]} {key}
        </span>
      ))}
    </>
  );
};

// Grade + finding count for a repo row, straight off the latest run's summary.
// security-scanner's findings.json shape: grade / finding_count / severity_counts.
const RunSummary = ({ summary }) => {
  if (!summary?.grade) return null;
  const count = summary.finding_count;
  return (
    <span className="sec-scan-summary">
      <span className={`sec-scan-grade sec-scan-grade-${String(summary.grade).toLowerCase()}`}>
        {summary.grade}
      </span>
      {Number.isFinite(count) && (
        <span className="sec-scan-muted">{count} finding{count === 1 ? '' : 's'}</span>
      )}
      <SeverityChips counts={summary.severity_counts} only={LISTED_SEVERITIES} />
    </span>
  );
};

const VERDICT_LABELS = {
  GO: 'Ready to deploy',
  READY_WITH_FIXES: 'Ready with fixes',
  NOT_READY: 'Not ready',
};

// Compact verdict badge for a repo row — code-review's findings.json
// shape: summary.verdict.{verdict, blocker_count, health_pct}.
const RunVerdictSummary = ({ summary }) => {
  const verdict = summary?.verdict;
  if (!verdict?.verdict) return null;
  return (
    <span className="sec-scan-summary">
      <span className={`sec-scan-verdict sec-scan-verdict-${verdict.verdict.toLowerCase()}`}>
        {VERDICT_LABELS[verdict.verdict] || verdict.verdict}
      </span>
      {Boolean(verdict.blocker_count) && (
        <span className="sec-scan-muted">
          {verdict.blocker_count} blocker{verdict.blocker_count === 1 ? '' : 's'}
        </span>
      )}
    </span>
  );
};

// Repo-list-row summary, dispatching on the scan type's summaryKind so the list
// doesn't need to know each skill's findings.json shape.
const ScanTypeRunSummary = ({ scanType, summary }) => (
  scanType.summaryKind === 'verdict'
    ? <RunVerdictSummary summary={summary} />
    : <RunSummary summary={summary} />
);

// Full detail-view summary for code-review: verdict banner,
// health bar, and an expandable list of the blockers that drove the verdict.
const VerdictDetailSummary = ({ summary }) => {
  const [expanded, setExpanded] = useState(false);
  const verdict = summary?.verdict;
  if (!verdict?.verdict) return null;
  const blockers = verdict.blocker_checks || [];
  return (
    <>
      <div className={`sec-scan-verdict-banner sec-scan-verdict-${verdict.verdict.toLowerCase()}`}>
        <strong>{VERDICT_LABELS[verdict.verdict] || verdict.verdict}</strong>
        <span>{verdict.summary}</span>
      </div>
      <div className="sec-scan-stats">
        <span>Health <strong>{verdict.health_pct ?? '—'}%</strong></span>
        <span>Blockers <strong>{verdict.blocker_count ?? 0}</strong></span>
      </div>
      {blockers.length > 0 && (
        <div className="sec-scan-summary">
          <button
            type="button"
            className="sec-scan-icon-btn sec-scan-blocker-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Hide blocker checks' : `Show ${blockers.length} blocker check${blockers.length === 1 ? '' : 's'}`}
          </button>
          {expanded && (
            <ul className="sec-scan-blocker-list">
              {blockers.map((check) => <li key={check}>{check}</li>)}
            </ul>
          )}
        </div>
      )}
      {summary?.severity_counts && (
        <div className="sec-scan-summary">
          <SeverityChips counts={summary.severity_counts} />
        </div>
      )}
    </>
  );
};

// Detail-view summary, dispatching on the scan type's summaryKind.
const ScanTypeDetailSummary = ({ scanType, summary }) => {
  if (scanType.summaryKind === 'verdict') {
    return summary?.verdict ? <VerdictDetailSummary summary={summary} /> : null;
  }
  if (!summary?.grade) return null;
  return (
    <>
      <div className="sec-scan-stats">
        <span>Grade <strong>{summary.grade}</strong></span>
        <span>Findings <strong>{summary.finding_count ?? '—'}</strong></span>
        <span>Health <strong>{summary.health_pct ?? '—'}%</strong></span>
      </div>
      <div className="sec-scan-summary">
        <SeverityChips counts={summary.severity_counts} />
      </div>
    </>
  );
};

const GarageSecurityScanView = ({ isAdmin, initialRunId, onOpenRun, onClearRun }) => {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // The agent is a global, admin-managed setting; non-admins only read it.
  const [agent, setAgent] = useState('claude');
  const [savingAgent, setSavingAgent] = useState(false);
  /** VM worker schedule + last scan activity for queued ETA. */
  const [workerEta, setWorkerEta] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [scope, setScope] = useState('mine');
  const [editRepo, setEditRepo] = useState(null);
  const [accessRepo, setAccessRepo] = useState(null);
  const [gitServiceUser, setGitServiceUser] = useState('the AI Garage service user');
  // FE env mirrors BE SEC_SCAN_WORKER_ACCESS_URL; settings.accessImmediate wins after load.
  const [accessImmediate, setAccessImmediate] = useState(
    () => config.SEC_SCAN_ACCESS_IMMEDIATE,
  );
  const [startingRepoId, setStartingRepoId] = useState(null);
  const [verifyingRepoId, setVerifyingRepoId] = useState(null);
  // Which repo row's "Scan" choice menu is open — at most one at a time, so a
  // single ref (attached only while that row's menu is actually mounted) is enough.
  const [scanMenuOpenFor, setScanMenuOpenFor] = useState(null);
  const scanMenuRef = useRef(null);
  const [deleteRepo, setDeleteRepo] = useState(null);
  const [deleteRun, setDeleteRun] = useState(null);
  const [deletingRepoId, setDeletingRepoId] = useState(null);
  const [deletingRunId, setDeletingRunId] = useState(null);

  // The open repo is DERIVED from `repos`, not snapshotted. Holding a copy meant
  // queuing or finishing a run left the detail view's Run button stale until a
  // manual refresh, because neither handleStart nor the poll re-copied it.
  const [openRepoId, setOpenRepoId] = useState(null);
  // Deep links can target a repo that isn't in the current list (other owner,
  // or scope=mine); that case falls back to the run's own repo fields.
  const [openRepoFallback, setOpenRepoFallback] = useState(null);
  // Which scan-type tab is showing in the repo detail view. Each tab's history is
  // independent — switching tabs re-fetches rather than filtering client-side, so
  // a repo with a long history on one scan type doesn't punish the other tab.
  const [activeScanType, setActiveScanType] = useState(DEFAULT_SCAN_TYPE);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Scan type the current `history` rows belong to. The loader stays up until
  // this matches the active tab, so a stale Security Scan response cannot
  // paint under the Code Review heading after the spinner hides.
  const [historySkill, setHistorySkill] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [dashboardUrl, setDashboardUrl] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-garage-theme') || 'light'
  );

  const openRepo = useMemo(
    () => (openRepoId
      ? repos.find((r) => r.id === openRepoId) || openRepoFallback
      : null),
    [openRepoId, repos, openRepoFallback]
  );

  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  });

  const showError = (title, err) => {
    setAlertModal({
      isOpen: true,
      title,
      message: formatApiError(err, title),
      variant: 'error',
    });
  };

  // Polling plus user-triggered loads means several /repos requests can be in
  // flight. Without a sequence guard a slow earlier response can land last and
  // resurrect stale state — e.g. re-enabling a Run button that should be blocked.
  const reposSeq = useRef(0);
  const loadRepos = useCallback(async () => {
    const seq = ++reposSeq.current;
    const rows = await garageAPI.listSecScanRepos({ scope });
    if (seq === reposSeq.current) setRepos(rows || []);
    return rows || [];
  }, [scope]);

  const loadAgent = useCallback(async () => {
    const data = await garageAPI.getSecScanSettings();
    if (data?.llmProvider) setAgent(data.llmProvider);
    if (data?.gitServiceUser) setGitServiceUser(data.gitServiceUser);
    if (typeof data?.accessImmediate === 'boolean') setAccessImmediate(data.accessImmediate);
    if (data) {
      setWorkerEta({
        workerIntervalMinutes: data.workerIntervalMinutes,
        workerBufferMinutes: data.workerBufferMinutes,
        lastWorkerActivityAt: data.lastWorkerActivityAt,
        skewMs: data.serverTime
          ? Date.now() - (toDate(data.serverTime)?.getTime() || Date.now())
          : 0,
      });
    }
    return data;
  }, []);

  const handleAgentChange = async (next) => {
    const previous = agent;
    setAgent(next);
    setSavingAgent(true);
    try {
      await garageAPI.updateSecScanSettings({ llmProvider: next });
    } catch (err) {
      setAgent(previous);
      showError('Could not change the agent', err);
    } finally {
      setSavingAgent(false);
    }
  };

  const handleEditRepo = async (payload) => {
    const updated = await garageAPI.updateSecScanRepo(editRepo.id, payload);
    const rows = await loadRepos();
    if (openRepoId === updated.id) {
      setOpenRepoFallback(rows.find((r) => r.id === updated.id) || updated);
    }
  };

  const historyTargetRef = useRef({ repoId: null, skillName: null, gen: 0 });

  const beginHistoryLoad = useCallback((repoId, skillName) => {
    const next = {
      repoId,
      skillName,
      gen: historyTargetRef.current.gen + 1,
    };
    historyTargetRef.current = next;
    setHistory([]);
    setHistorySkill(null);
    setHistoryLoading(true);
    return next;
  }, []);

  const loadHistory = useCallback(async (repoId, skillName, gen) => {
    const expectedGen = gen ?? historyTargetRef.current.gen;
    const rows = await garageAPI.listSecScanRuns({ repo_id: repoId, scope, skill_name: skillName });
    const cur = historyTargetRef.current;
    if (cur.repoId === repoId && cur.skillName === skillName && cur.gen === expectedGen) {
      setHistory(rows || []);
      setHistorySkill(skillName);
      setHistoryLoading(false);
    }
    return rows || [];
  }, [scope]);

  const finishHistoryLoadError = (gen, skillName) => {
    if (historyTargetRef.current.gen !== gen) return;
    setHistoryLoading(false);
    // Mark the tab as resolved so the loader doesn't sit forever; the empty
    // state (and the error modal) is the honest outcome of a failed fetch.
    setHistorySkill(skillName);
  };

  // Full-screen loader on first paint only; a scope switch just spins the refresh icon.
  const firstLoadDone = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (firstLoadDone.current) setRefreshing(true);
      try {
        await Promise.all([loadRepos(), loadAgent().catch(() => {})]);
      } catch (err) {
        if (!cancelled) showError('Failed to load security scans', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
          firstLoadDone.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRepos, loadAgent]);

  const clearDashboard = () => {
    setDashboardLoading(false);
    setDashboardUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const loadRun = useCallback(async (runId, { announce = true } = {}) => {
    const data = await garageAPI.getSecScanRun(runId);
    setActiveRun(data);
    if (announce && onOpenRun) onOpenRun(runId);
    const hasDashboard = (data.artifacts || []).some((a) => a.kind === 'dashboard_html');
    if (data.status === 'succeeded' && hasDashboard) {
      // Fetching the report can take several seconds, so say so meanwhile.
      setDashboardLoading(true);
      try {
        const blobUrl = await garageAPI.getSecScanDashboardObjectUrl(runId);
        setDashboardUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return blobUrl;
        });
      } finally {
        setDashboardLoading(false);
      }
    } else {
      clearDashboard();
    }
    return data;
  }, [onOpenRun]);

  // skillName lets a card's scan tile open the repo straight onto that scan
  // type's tab, instead of always landing on the default and making the user
  // switch tabs to see the result they just clicked.
  const openRepoView = useCallback(async (repo, skillName = DEFAULT_SCAN_TYPE) => {
    setOpenRepoId(repo.id);
    setOpenRepoFallback(repo);
    setActiveRun(null);
    setActiveScanType(skillName);
    clearDashboard();
    // Drop the previous repo's runs before fetching: leaving them mounted showed
    // one repo's history under another repo's heading until the fetch landed.
    const req = beginHistoryLoad(repo.id, skillName);
    try {
      await loadHistory(req.repoId, req.skillName, req.gen);
    } catch (err) {
      finishHistoryLoadError(req.gen, req.skillName);
      showError('Failed to load scan history', err);
    }
  }, [beginHistoryLoad, loadHistory]);

  // Switching tabs inside an open repo — independent fetch per scan type, not a
  // client-side filter, so one tab's history size never affects the other's load.
  const switchScanType = useCallback(async (skillName) => {
    if (!openRepo || skillName === activeScanType || historyLoading) return;
    setActiveScanType(skillName);
    setActiveRun(null);
    clearDashboard();
    // Same reason as above — these rows belong to the tab being left behind, so
    // they must not linger under the incoming tab's heading while it loads.
    // Loader stays up until a response for THIS (repo, skill, gen) lands;
    // a stale poll for the previous tab must not hide it or paint those rows.
    const req = beginHistoryLoad(openRepo.id, skillName);
    try {
      await loadHistory(req.repoId, req.skillName, req.gen);
    } catch (err) {
      finishHistoryLoadError(req.gen, req.skillName);
      showError('Failed to load scan history', err);
    }
  }, [openRepo, activeScanType, historyLoading, beginHistoryLoad, loadHistory]);

  // Deep link: /ai-garage/sec-scan/:runId opens that run inside its repo, on
  // whichever scan-type tab that run actually belongs to.
  useEffect(() => {
    if (!initialRunId || loading) return;
    if (activeRun?.id === initialRunId) return;
    (async () => {
      try {
        const run = await loadRun(initialRunId, { announce: false });
        const skillName = run.skillName || DEFAULT_SCAN_TYPE;
        // The repo may not be in the current list (other owner, or scope=mine),
        // so fall back to the run's own repo fields rather than dropping the report.
        const repo = repos.find((r) => r.id === run.repoId) || {
          id: run.repoId,
          displayName: run.repoDisplayName || 'Repository',
          gitUrl: run.gitUrl,
          defaultRef: run.ref || null,
        };
        setOpenRepoId(repo.id);
        setOpenRepoFallback(repo);
        setActiveScanType(skillName);
        const req = beginHistoryLoad(repo.id, skillName);
        try {
          await loadHistory(req.repoId, req.skillName, req.gen);
        } catch (err) {
          finishHistoryLoadError(req.gen, req.skillName);
          throw err;
        }
      } catch (err) {
        showError('Failed to load scan', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId, loading]);

  // Anything in flight anywhere on screen is worth polling for — not just an open report.
  const hasPendingAccess = useMemo(
    () => repos.some((repo) => ACCESS_PENDING.has(accessStatusOf(repo)))
      || ACCESS_PENDING.has(accessStatusOf(openRepo)),
    [repos, openRepo],
  );

  const hasActiveWork = useMemo(() => (
    isActive(activeRun)
    || history.some(isActive)
    || hasPendingAccess
    || repos.some((repo) => (
      SCAN_TYPES.some((t) => isActive(repo.latestRunsBySkill?.[t.key]))
    ))
  ), [activeRun, history, repos, hasPendingAccess]);

  // Poll reads live refs, not closed-over state: a tick that started on
  // Security Scan can still be sitting in `await loadRepos()` when the user
  // switches to Code Review. historyTargetRef is updated synchronously in
  // beginHistoryLoad (before the switch's fetch), so an in-flight tick either
  // fetches the new tab or has its old-tab response discarded.
  const pollTargetRef = useRef({});
  pollTargetRef.current = { activeRun };

  // Held in a ref so the interval below isn't torn down and rebuilt on every tick.
  const pollRef = useRef(() => {});
  pollRef.current = async () => {
    try {
      await loadRepos();
      await loadAgent().catch(() => {});
      const { repoId, skillName, gen } = historyTargetRef.current;
      if (repoId) await loadHistory(repoId, skillName, gen);
      const { activeRun: curRun } = pollTargetRef.current;
      if (isActive(curRun)) await loadRun(curRun.id, { announce: false });
    } catch {
      // Transient failures are fine; the next tick retries.
    }
  };

  // Assigned after applyAccessResult; interval only calls .current.
  const accessRefreshInFlight = useRef(false);
  const accessRefreshRef = useRef(() => {});

  useEffect(() => {
    if (!hasActiveWork) return undefined;
    const timer = setInterval(() => pollRef.current(), POLL_MS);
    return () => clearInterval(timer);
  }, [hasActiveWork]);

  // Re-trigger access validation for pending/checking repos on a slower cadence.
  useEffect(() => {
    const mins = config.SEC_SCAN_ACCESS_REFRESH_MINUTES;
    if (!mins || !hasPendingAccess) return undefined;
    const timer = setInterval(() => {
      accessRefreshRef.current();
    }, mins * MINUTE);
    return () => clearInterval(timer);
  }, [hasPendingAccess]);

  // Tick the queued-scan ETA once a second while something is pending.
  const hasQueued = useMemo(
    () => history.some((r) => r.status === 'pending')
      || repos.some((r) => (
        SCAN_TYPES.some((t) => r.latestRunsBySkill?.[t.key]?.status === 'pending')
      ))
      || activeRun?.status === 'pending',
    [history, repos, activeRun]
  );

  useEffect(() => {
    if (!hasQueued) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasQueued]);

  useEffect(() => () => {
    if (dashboardUrl) URL.revokeObjectURL(dashboardUrl);
  }, [dashboardUrl]);

  // The report reads "#theme=..." once, at load, so remount the iframe (via key)
  // when the dashboard theme changes.
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setTheme(root.getAttribute('data-garage-theme') || 'light');
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-garage-theme'] });
    return () => observer.disconnect();
  }, []);

  // Per-row "Scan" choice menu — same click-outside/Escape pattern as the user
  // menu in AIGaragePage.jsx.
  useEffect(() => {
    if (!scanMenuOpenFor) return undefined;
    const onDocClick = (e) => {
      if (scanMenuRef.current && !scanMenuRef.current.contains(e.target)) {
        setScanMenuOpenFor(null);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setScanMenuOpenFor(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [scanMenuOpenFor]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (openRepo) {
        await loadRepos();
        await loadHistory(openRepo.id, activeScanType);
        if (activeRun) await loadRun(activeRun.id, { announce: false });
      } else {
        await loadRepos();
      }
    } catch (err) {
      showError('Refresh failed', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddRepo = async (payload) => {
    const created = await garageAPI.createSecScanRepo(payload);
    await loadRepos();
    if (created?.accessStatus === 'verified') {
      setAlertModal({
        isOpen: true,
        title: 'Access verified',
        message: created.accessMessage || 'The service user can read this repository. You can start a scan.',
        variant: 'success',
      });
      return;
    }
    if (created?.accessStatus === 'denied') {
      setAccessRepo(created);
    }
  };

  const applyAccessResult = (updated) => {
    if (updated?.accessStatus === 'verified') {
      setAccessRepo(null);
      setAlertModal({
        isOpen: true,
        title: 'Access verified',
        message: updated.accessMessage || 'Repository access is verified. You can start a scan.',
        variant: 'success',
      });
    } else if (updated?.accessStatus === 'denied') {
      setAccessRepo(updated);
    } else {
      setAccessRepo(null);
    }
    return updated;
  };

  const handleGrantAccess = async (accessToken) => {
    if (!accessRepo?.id) return null;
    let payload = {};
    if (accessToken) {
      // Transport key (FE) → BE unwraps → storage key (Fernet) for DB/worker.
      payload = { accessToken: await encryptAccessTokenForTransport(accessToken) };
    }
    const updated = await garageAPI.grantSecScanRepoAccess(accessRepo.id, payload);
    await loadRepos();
    return applyAccessResult(updated);
  };

  const handleVerifyAccess = async (repo) => {
    if (!repo?.id || verifyingRepoId) return;
    setVerifyingRepoId(repo.id);
    try {
      const updated = await garageAPI.grantSecScanRepoAccess(repo.id, {});
      await loadRepos();
      applyAccessResult(updated);
    } catch (err) {
      showError('Could not verify access', err);
    } finally {
      setVerifyingRepoId(null);
    }
  };

  // Quiet re-check for repos still pending/checking (no modal spam on denied).
  accessRefreshRef.current = async () => {
    if (accessRefreshInFlight.current) return;
    const pending = repos.filter((repo) => ACCESS_PENDING.has(accessStatusOf(repo)));
    if (!pending.length) return;
    accessRefreshInFlight.current = true;
    try {
      let verified = null;
      for (const repo of pending) {
        try {
          const updated = await garageAPI.grantSecScanRepoAccess(repo.id, {});
          if (updated?.accessStatus === 'verified') verified = updated;
        } catch {
          // Next interval retries.
        }
      }
      await loadRepos();
      if (verified) applyAccessResult(verified);
    } finally {
      accessRefreshInFlight.current = false;
    }
  };

  // Queues a run for the given scan type (defaults to whichever tab is open) and
  // stays on the current screen.
  const handleStart = async (repoId, skillName = activeScanType) => {
    const target = repos.find((r) => r.id === repoId) || (openRepo?.id === repoId ? openRepo : null);
    if (accessStatusOf(target) === 'denied') {
      setAccessRepo(target);
      return;
    }
    if (scanDisabledForAccess(target, accessImmediate)) {
      return;
    }
    setStartingRepoId(repoId);
    try {
      const job = await garageAPI.startSecScanRun(repoId, { skillName });
      // Reflect the new run immediately; loadRepos() below confirms it.
      if (job?.id) {
        setRepos((prev) => prev.map((r) => (
          r.id === repoId
            ? {
              ...r,
              latestRunsBySkill: { ...r.latestRunsBySkill, [skillName]: job },
              activeRunForCurrentRefBySkill: { ...r.activeRunForCurrentRefBySkill, [skillName]: job },
            }
            : r
        )));
        setOpenRepoFallback((prev) => (
          prev && prev.id === repoId
            ? {
              ...prev,
              latestRunsBySkill: { ...prev.latestRunsBySkill, [skillName]: job },
              activeRunForCurrentRefBySkill: { ...prev.activeRunForCurrentRefBySkill, [skillName]: job },
            }
            : prev
        ));
      }
      await loadRepos();
      if (openRepo?.id === repoId) await loadHistory(repoId, skillName);
    } catch (err) {
      showError('Could not start scan', err);
    } finally {
      setStartingRepoId(null);
    }
  };

  const backToList = () => {
    setOpenRepoId(null);
    setOpenRepoFallback(null);
    setActiveRun(null);
    historyTargetRef.current = {
      repoId: null,
      skillName: null,
      gen: historyTargetRef.current.gen + 1,
    };
    setHistory([]);
    setHistorySkill(null);
    setHistoryLoading(false);
    clearDashboard();
    if (onClearRun) onClearRun();
  };

  const closeReport = () => {
    setActiveRun(null);
    clearDashboard();
    if (onClearRun) onClearRun();
  };

  const handleDelete = async () => {
    if (!deleteRepo || deletingRepoId) return;
    const removedId = deleteRepo.id;
    setDeletingRepoId(removedId);
    try {
      await garageAPI.deleteSecScanRepo(removedId);
      setDeleteRepo(null);
      if (openRepo?.id === removedId) backToList();
      await loadRepos();
    } catch (err) {
      showError('Could not remove repository', err);
    } finally {
      setDeletingRepoId(null);
    }
  };

  const handleDeleteRun = async () => {
    if (!deleteRun || !openRepo || deletingRunId) return;
    const removedId = deleteRun.id;
    setDeletingRunId(removedId);
    try {
      await garageAPI.deleteSecScanRun(removedId);
      setDeleteRun(null);
      if (activeRun?.id === removedId) closeReport();
      await loadHistory(openRepo.id, activeScanType);
      await loadRepos();
    } catch (err) {
      showError('Could not delete scan run', err);
    } finally {
      setDeletingRunId(null);
    }
  };

  const downloadKind = async (kind, fileName) => {
    if (!activeRun) return;
    try {
      await garageAPI.downloadSecScanArtifact(activeRun.id, kind, fileName);
    } catch (err) {
      showError('Download failed', err);
    }
  };

  if (loading) {
    return <GarageLoadingScreen message="Loading scans…" />;
  }

  const modals = (
    <>
      <AddSecScanRepoModal
        isOpen={showAddModal}
        serviceUser={gitServiceUser}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddRepo}
      />
      <EditSecScanRepoModal
        isOpen={Boolean(editRepo)}
        repo={editRepo}
        onClose={() => setEditRepo(null)}
        onSubmit={handleEditRepo}
      />
      <GrantSecScanAccessModal
        isOpen={Boolean(accessRepo)}
        repo={accessRepo}
        serviceUser={gitServiceUser}
        onClose={() => setAccessRepo(null)}
        onDone={handleGrantAccess}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal((s) => ({ ...s, isOpen: false }))}
      />
      <ConfirmModal
        isOpen={Boolean(deleteRepo)}
        title="Delete repository"
        message={deleteRepo
          ? `Delete ${deleteRepo.displayName}, all of its scan runs, and all generated files?`
          : ''}
        confirmText="Delete"
        loadingText="Deleting…"
        isLoading={Boolean(deletingRepoId)}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteRepo(null)}
      />
      <ConfirmModal
        isOpen={Boolean(deleteRun)}
        title="Delete scan run"
        message={deleteRun
          ? `Delete the scan from ${formatWhen(deleteRun.createdAt)} and all of its generated files?`
          : ''}
        confirmText="Delete"
        loadingText="Deleting…"
        isLoading={Boolean(deletingRunId)}
        variant="danger"
        onConfirm={handleDeleteRun}
        onCancel={() => setDeleteRun(null)}
      />
    </>
  );

  // Admin-only: one global choice, applied to every scan.
  const agentField = isAdmin ? (
    <label className="sec-scan-field">
      <span className="sec-scan-field-label">Agent (all scans)</span>
      <select
        className="garage-select"
        value={agent}
        disabled={savingAgent}
        onChange={(e) => handleAgentChange(e.target.value)}
        title="Agent used for every new scan, for all users"
      >
        <option value="claude">Claude</option>
        <option value="codex">Codex</option>
      </select>
    </label>
  ) : null;

  const refreshButton = (
    <button
      type="button"
      className="garage-btn garage-btn-secondary sec-scan-icon-only"
      onClick={handleRefresh}
      disabled={refreshing}
      title="Refresh status"
      aria-label="Refresh status"
    >
      <RefreshCw size={16} className={refreshing ? 'sec-scan-spin' : undefined} />
    </button>
  );

  if (openRepo) {
    const summary = activeRun?.summary || {};
    const progress = activeRun?.progress || [];
    const currentScanType = getScanType(activeScanType);
    const openRepoBlocked = scanBlockedReason(openRepo, activeScanType, accessImmediate);
    const historyPending = historyLoading || historySkill !== activeScanType;
    return (
      <div className="garage-content sec-scan">
        <div className="sec-scan-header">
          <button type="button" className="garage-btn garage-btn-secondary" onClick={backToList}>
            <ArrowLeft size={16} />
            Repositories
          </button>
          <div className="sec-scan-header-actions">
            {agentField}
            {refreshButton}
            {accessImmediate && (
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                disabled={verifyingRepoId === openRepo.id}
                title="Re-check service-user or stored-token access"
                onClick={() => handleVerifyAccess(openRepo)}
              >
                <KeyRound size={16} />
                {verifyingRepoId === openRepo.id ? 'Checking…' : 'Verify access'}
              </button>
            )}
            <span title={openRepoBlocked || undefined}>
              <button
                type="button"
                className="garage-btn garage-btn-primary"
                disabled={startingRepoId === openRepo.id || Boolean(openRepoBlocked)}
                onClick={() => handleStart(openRepo.id)}
              >
                <Play size={16} />
                {startingRepoId === openRepo.id ? 'Queuing…' : 'Run scan'}
              </button>
            </span>
          </div>
        </div>

        <div className="sec-scan-card">
          <div className="sec-scan-run-title">
            <h2>{openRepo.displayName}</h2>
            <BranchTag gitRef={openRepo.defaultRef} />
            <button
              type="button"
              className="sec-scan-icon-btn"
              title="Edit name or branch"
              aria-label="Edit name or branch"
              onClick={() => setEditRepo(openRepo)}
            >
              <Pencil size={14} />
            </button>
          </div>
          <p className="sec-scan-muted">
            {openRepo.gitUrl}
            {isAdmin && openRepo.createdBy ? ` · added by ${openRepo.createdBy}` : ''}
          </p>
          <AccessHint repo={openRepo} accessImmediate={accessImmediate} serviceUser={gitServiceUser} />
          {openRepoBlocked && accessStatusOf(openRepo) !== 'denied' && (
            <p className="sec-scan-notice">{openRepoBlocked}</p>
          )}
          {accessStatusOf(openRepo) === 'denied' && (
            <button
              type="button"
              className="garage-btn garage-btn-secondary garage-btn-sm"
              onClick={() => setAccessRepo(openRepo)}
            >
              Grant access
            </button>
          )}
        </div>

        <div className="sec-scan-type-tabs" role="tablist" aria-label="Scan type">
          {SCAN_TYPES.map((t) => {
            const Icon = t.icon;
            const isCurrent = activeScanType === t.key;
            const busy = isCurrent && historyPending;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={isCurrent}
                aria-busy={busy}
                disabled={historyPending && !isCurrent}
                className={`sec-scan-type-tab${isCurrent ? ' active' : ''}`}
                onClick={() => switchScanType(t.key)}
              >
                {busy
                  ? <Loader2 size={14} className="sec-scan-spin" />
                  : <Icon size={14} />}
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="sec-scan-card">
          <h2>Scan history — {currentScanType.label}</h2>
          {/* Keep the loader until the rows in `history` actually belong to
              this tab. historyLoading going false is not enough: a stale poll
              for the previous tab can hide the spinner while those old rows
              are still (or again) in state. */}
          {historyPending ? (
            <div className="sec-scan-loading" role="status" aria-live="polite">
              <Loader2 size={18} className="sec-scan-spin" />
              <span>Loading {currentScanType.label} history…</span>
            </div>
          ) : history.length === 0 ? (
            <p className="sec-scan-empty">No scans yet. Click Run scan to queue one.</p>
          ) : (
            <div className="sec-scan-table-scroll">
              <table className="sec-scan-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Agent</th>
                    <th>Branch</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => (
                    <tr key={run.id} className={activeRun?.id === run.id ? 'open' : undefined}>
                      <td>{formatWhen(run.createdAt)}</td>
                      <td>{providerLabel(run.llmProvider)}</td>
                      {/* The run's own ref: editing the repo later must not rewrite history. */}
                      <td><BranchTag gitRef={run.ref} /></td>
                      <td>{formatDuration(run.startedAt, run.finishedAt)}</td>
                      <td><span className={statusClass(run.status)}>{statusLabel(run.status)}</span></td>
                      <td className="sec-scan-row-actions">
                        <button
                          type="button"
                          className="garage-btn garage-btn-secondary garage-btn-sm"
                          onClick={() => loadRun(run.id).catch((err) => showError('Failed to load scan', err))}
                        >
                          <FileText size={14} />
                          {activeRun?.id === run.id ? 'Viewing' : 'Open'}
                        </button>
                        <button
                          type="button"
                          className="sec-scan-icon-btn"
                          title="Delete run and generated files"
                          aria-label="Delete run and generated files"
                          disabled={deletingRunId === run.id}
                          aria-busy={deletingRunId === run.id}
                          onClick={() => setDeleteRun(run)}
                        >
                          {deletingRunId === run.id
                            ? <Loader2 size={14} className="sec-scan-spin" />
                            : <Trash2 size={14} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {activeRun && (
          <div className="sec-scan-card">
            <div className="sec-scan-run-title">
              <h2>Scan from {formatWhen(activeRun.createdAt)}</h2>
              <span className={statusClass(activeRun.status)}>{statusLabel(activeRun.status)}</span>
              <button
                type="button"
                className="sec-scan-icon-btn"
                onClick={closeReport}
                title="Close report"
                aria-label="Close report"
              >
                <XCircle size={16} />
              </button>
            </div>
            <p className="sec-scan-muted">
              {providerLabel(activeRun.llmProvider)}
              {activeRun.llmModel ? ` · ${activeRun.llmModel}` : ''}
              {activeRun.ref ? ` · ${activeRun.ref}` : ''}
            </p>

            {ACTIVE.has(activeRun.status) && (
              <>
                <div className="sec-scan-bar">
                  <div style={{ width: `${activeRun.progressPct || 0}%` }} />
                </div>
                <p className="sec-scan-muted">
                  {activeRun.progressMessage || activeRun.progressStage || 'Waiting for worker…'}
                  {activeRun.status === 'pending' && (
                    <>
                      {' · '}
                      <PickupEstimate
                        worker={workerEta}
                        now={now}
                        queuedAt={activeRun.createdAt}
                      />
                    </>
                  )}
                </p>
                {progress.length > 1 && (
                  <ol className="sec-scan-trail">
                    {progress.slice(-6).map((event) => (
                      <li key={event.id}>
                        <span>{event.message || event.stage}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}

            {activeRun.errorMessage && (
              <div className="garage-error-message">{activeRun.errorMessage}</div>
            )}

            <ScanTypeDetailSummary scanType={getScanType(activeRun.skillName)} summary={summary} />

            {(activeRun.artifacts || []).length > 0 && (
              <div className="sec-scan-downloads">
                {[...(activeRun.artifacts || [])]
                  .sort((a, b) => {
                    const order = {
                      plan_md: 0,
                      deployment_todos_json: 1,
                      dashboard_html: 2,
                      report_md: 3,
                      report_xlsx: 4,
                      findings_json: 5,
                    };
                    return (order[a.kind] ?? 50) - (order[b.kind] ?? 50);
                  })
                  .map((a) => (
                    <button
                      key={a.kind}
                      type="button"
                      className="garage-btn garage-btn-secondary garage-btn-sm"
                      onClick={() => downloadKind(a.kind, a.fileName)}
                    >
                      <Download size={14} />
                      {a.fileName || a.kind}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {(dashboardLoading || dashboardUrl) && (
          <div className="sec-scan-card sec-scan-report">
            <div className="sec-scan-run-title">
              <h2>Report</h2>
              {dashboardUrl && (
                <a
                  className="garage-btn garage-btn-secondary garage-btn-sm"
                  href={dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={14} />
                  Open full report
                </a>
              )}
            </div>
            {dashboardUrl ? (
              <iframe
                key={theme}
                title="Security scan report"
                src={`${dashboardUrl}#theme=${theme}`}
                sandbox="allow-scripts"
              />
            ) : (
              <p className="sec-scan-empty">
                <Loader2 size={14} className="sec-scan-spin" /> Loading report…
              </p>
            )}
          </div>
        )}

        {modals}
      </div>
    );
  }

  return (
    <div className={`garage-content sec-scan${isAdmin ? ' is-admin' : ''}`}>
      <div className="sec-scan-header">
        <h1>Scans</h1>
        <div className="sec-scan-header-actions">
          {isAdmin && (
            <label className="sec-scan-field">
              <span className="sec-scan-field-label">Show</span>
              <select
                className="garage-select"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                title="Which repositories to list"
              >
                <option value="mine">My repositories</option>
                <option value="all">All repositories</option>
              </select>
            </label>
          )}
          {agentField}
          {refreshButton}
          <button
            type="button"
            className="garage-btn garage-btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            Add
          </button>
        </div>
      </div>

      {repos.length === 0 ? (
        <div className="sec-scan-card">
          <p className="sec-scan-empty">No repositories yet. Click Add to register one.</p>
        </div>
      ) : (
          /* Cards rather than table rows: each repo carries two scan types, so a
             row had to line up four structurally different cells (2-line repo
             block, 1-line owner, 2-line status stack, buttons) across every row
             and both scan types. A card owns its own layout, and each scan tile
             is labelled in place, so nothing depends on cross-row alignment. */
          <div className="sec-scan-repo-list">
            {repos.map((repo) => (
              <article
                key={repo.id}
                className="sec-scan-repo-card"
                onClick={() => openRepoView(repo)}
                title="Open scan history"
              >
                <div className="sec-scan-repo-main">
                  {/* Not a nested button: the whole card already opens the repo,
                      and a button-inside-a-clickable-card is invalid ARIA
                      (interactive controls can't nest). Keyboard/AT users tab to
                      the scan tiles and action buttons below instead, which cover
                      the same destinations. */}
                  <span className="sec-scan-repo-title">
                    <strong>{repo.displayName}</strong>
                    <BranchTag gitRef={repo.defaultRef} />
                  </span>
                  <p className="sec-scan-repo-url" title={repo.gitUrl}>{repo.gitUrl}</p>
                  <AccessHint repo={repo} accessImmediate={accessImmediate} serviceUser={gitServiceUser} />
                  {isAdmin && (
                    <p className="sec-scan-repo-owner" title={repo.createdBy}>
                      {repo.createdBy || '—'}
                    </p>
                  )}
                </div>

                <div className="sec-scan-repo-scans">
                  {/* One tile per scan type, each opening a SPECIFIC tab — a more
                      precise action than the card's own (default-tab) click, so
                      it stops propagation rather than letting the card's handler
                      re-fire and override the tab choice back to the default. */}
                  {SCAN_TYPES.map((t) => {
                    const latest = repo.latestRunsBySkill?.[t.key];
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        className="sec-scan-scan-tile"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRepoView(repo, t.key);
                        }}
                        title={`Open ${t.label}`}
                      >
                        <span className="sec-scan-tile-head">
                          <Icon size={13} />
                          <span className="sec-scan-tile-label">{t.label}</span>
                        </span>
                        <span className="sec-scan-tile-body">
                          {latest ? (
                            <>
                              <span className={statusClass(latest.status)}>
                                {statusLabel(latest.status)}
                              </span>
                              {latest.status === 'pending' ? (
                                <PickupEstimate
                                  worker={workerEta}
                                  now={now}
                                  queuedAt={latest.createdAt}
                                />
                              ) : (
                                <ScanTypeRunSummary scanType={t} summary={latest.summary} />
                              )}
                            </>
                          ) : (
                            <span className="sec-scan-muted">Never scanned</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* One stopPropagation here covers Scan/Edit/Delete (and the scan
                    dropdown items) — cheaper than repeating it on every button,
                    and correct because a click always bubbles through this
                    wrapper before it could reach the card's onClick. */}
                <div className="sec-scan-repo-actions" onClick={(e) => e.stopPropagation()}>
                    {accessStatusOf(repo) === 'denied' && (
                      <button
                        type="button"
                        className="garage-btn garage-btn-secondary garage-btn-sm"
                        onClick={() => setAccessRepo(repo)}
                      >
                        Grant access
                      </button>
                    )}
                    {accessImmediate && (
                      <button
                        type="button"
                        className="garage-btn garage-btn-secondary garage-btn-sm"
                        disabled={verifyingRepoId === repo.id}
                        title="Re-check service-user or stored-token access"
                        onClick={() => handleVerifyAccess(repo)}
                      >
                        <KeyRound size={14} />
                        {verifyingRepoId === repo.id ? 'Checking…' : 'Verify access'}
                      </button>
                    )}
                    <div
                      className="sec-scan-scan-menu"
                      ref={scanMenuOpenFor === repo.id ? scanMenuRef : undefined}
                    >
                      <button
                        type="button"
                        className="garage-btn garage-btn-secondary garage-btn-sm"
                        disabled={startingRepoId === repo.id || scanDisabledForAccess(repo, accessImmediate)}
                        aria-haspopup="menu"
                        aria-expanded={scanMenuOpenFor === repo.id}
                        title={scanDisabledForAccess(repo, accessImmediate) ? (scanBlockedReason(repo, DEFAULT_SCAN_TYPE, accessImmediate) || ACCESS_BLOCK) : undefined}
                        onClick={() => {
                          if (scanDisabledForAccess(repo, accessImmediate)) return;
                          setScanMenuOpenFor((v) => (v === repo.id ? null : repo.id));
                        }}
                      >
                        <Play size={14} />
                        {startingRepoId === repo.id ? 'Queuing…' : 'Scan'}
                        <ChevronDown size={12} />
                      </button>
                      {scanMenuOpenFor === repo.id && !scanDisabledForAccess(repo, accessImmediate) && (
                        <div className="sec-scan-scan-dropdown" role="menu">
                          {SCAN_TYPES.map((t) => {
                            const Icon = t.icon;
                            const rowBlocked = scanBlockedReason(repo, t.key, accessImmediate);
                            return (
                              <button
                                key={t.key}
                                type="button"
                                role="menuitem"
                                className="sec-scan-scan-dropdown-item"
                                disabled={Boolean(rowBlocked)}
                                title={rowBlocked || undefined}
                                onClick={() => {
                                  setScanMenuOpenFor(null);
                                  handleStart(repo.id, t.key);
                                }}
                              >
                                <Icon size={14} />
                                {t.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="sec-scan-icon-btn"
                      title="Edit name or branch"
                      aria-label="Edit name or branch"
                      onClick={() => setEditRepo(repo)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="sec-scan-icon-btn"
                      title="Delete repository, runs, and generated files"
                      aria-label="Delete repository, runs, and generated files"
                      disabled={deletingRepoId === repo.id}
                      aria-busy={deletingRepoId === repo.id}
                      onClick={() => setDeleteRepo(repo)}
                    >
                      {deletingRepoId === repo.id
                        ? <Loader2 size={14} className="sec-scan-spin" />
                        : <Trash2 size={14} />}
                    </button>
                </div>
              </article>
            ))}
          </div>
      )}

      {modals}
    </div>
  );
};

export default GarageSecurityScanView;
